import { Cubit } from "blac";
import { translate } from "lib/blocs/TranslateionBloc";
import srt2webvtt from "lib/srt2webvtt";
import { Language, Languages } from "src/constants/language";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import type { TrackEvent } from "src/state/Track/TrackCubit";
import { VideoPlayerLibInterface } from "src/state/VideoPlayerCubit/VideoPlayerCubit";
import { tracker } from "src/state/state";
import videojs from "video.js";

export type FullVideoPlayerBlocState = {
  playing: boolean;
  ready: boolean;
  player?: VideoPlayerLibInterface;
  errorMessage?: string;
  providerErrorMessage?: string;
};

export type VideoTrack = {
  kind: "captions";
  default?: boolean;
  src: string;
  /* language in valid BCP 47 */
  srclang: string;
  label: string;
  format: "srt" | "vtt";
};

export type VideoPlayerOptions = {
  autoplay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  poster?: string;
  preload?: "auto" | "metadata" | "none";
  ratio?: number;
  nativeControlsForTouch?: boolean;
  playsinline?: boolean;
  sources?: {
    src: string;
    type: string;
  }[];
  html5?: {
    nativeTextTracks?: boolean;
  };
  tracks?: VideoTrack[];
};

export default class FullVideoPlayerBloc extends Cubit<FullVideoPlayerBlocState> {
  containerElement: HTMLVideoElement | null = null;
  videoOptions?: VideoPlayerOptions;
  defaultOptions: VideoPlayerOptions = {
    autoplay: false,
    controls: true,
    loop: false,
    muted: false,
    preload: "auto",
    playsinline: true,
    //  when the app is wrapped in a webview or inappbrowser
    nativeControlsForTouch: true,
    html5: {
      nativeTextTracks: true
    }
  };

  constructor() {
    super({
      playing: false,
      ready: false,
      errorMessage: undefined,
      providerErrorMessage: undefined
    });
  }

  initVideo = async (
    containerElement: HTMLVideoElement | null,
    videoOptions: VideoPlayerOptions
  ) => {
    const tracks = await this.parseTracks(videoOptions.tracks ?? []);
    const optionsMerged = {
      ...this.defaultOptions,
      ...videoOptions,
      tracks
    };
    if (this.containerElement !== null || containerElement === null) {
      return;
    }

    this.containerElement = containerElement;
    this.videoOptions = optionsMerged;

    for (const track of tracks) {
      const trackElement = document.createElement("track");
      trackElement.src = track.src;
      trackElement.srclang = track.srclang;
      trackElement.label = track.label;
      trackElement.default = track.default ?? false;
      this.containerElement.appendChild(trackElement);
    }

    const player = videojs(this.containerElement, optionsMerged, () => {
      this.emit({
        ...this.state,
        playing: videoOptions.autoplay === true,
        ready: true,
        player: player as unknown as VideoPlayerLibInterface
      });
      player.preload();

      this.addPlayerListeners();
    });
  };

  safeFetchTrackAndParse = async (tracks: VideoTrack[]): Promise<string[]> => {
    const promises = tracks.map((track) => {
      return async (): Promise<string> => {
        try {
          const contentResponse = await fetch(track.src);
          const content = await contentResponse.text();
          const text = srt2webvtt(content, {
            delay: 3800 // 3.8 seconds delay to sync the captions with the video, the intro from Sanctuary Health is about that long
          });

          const vttBlob = new Blob([text], { type: "text/vtt" });
          const blobURL = URL.createObjectURL(vttBlob);
          return blobURL;
        } catch (error) {
          reportErrorSentry(error);
          return "";
        }
      };
    });

    const vttData = await Promise.all(promises.map((p) => p()));
    return vttData.filter((vtt) => vtt !== "");
  };

  parseTracks = async (tracks: VideoTrack[]): Promise<VideoTrack[]> => {
    const parsedTracks: VideoTrack[] = [];

    const srtTracks = tracks.filter((track) => track.format === "srt");
    const supportedTracks = srtTracks.filter((track) =>
      Languages.includes(track.srclang as Language)
    );

    const vttData = await this.safeFetchTrackAndParse(supportedTracks);

    const currentLanguage = StorageController.getItem("videoPlayerLanguage");
    for (let i = 0; i < supportedTracks.length; i++) {
      parsedTracks.push({
        ...supportedTracks[i],
        src: vttData[i],
        format: "vtt",
        default: currentLanguage === supportedTracks[i].srclang
      });
    }

    return parsedTracks;
  };

  errorCount = 0;
  errorLimit = 3;
  networkTimeoutId?: ReturnType<typeof setTimeout>;
  lastProgressTime = 0;
  isSafari =
    typeof navigator !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  addPlayerListeners = () => {
    if (!this.state.player) {
      return;
    }

    const trackingEvents = ["play", "error"];

    const { player } = this.state;

    let currentLanguage = StorageController.getItem("videoPlayerLanguage");
    if (!currentLanguage) {
      currentLanguage = "OFF";
    }

    player.on("texttrackchange", () => {
      const tracks = player.textTracks();
      const activeTrack = tracks.tracks_.find(
        (track) => track.mode === "showing"
      );
      const language = activeTrack?.language ?? "";
      const newLanguage = language === "" ? "OFF" : language;
      StorageController.setItem("videoPlayerLanguage", newLanguage);

      if (currentLanguage !== newLanguage) {
        currentLanguage = newLanguage;
      }
    });

    trackingEvents.forEach((eventName) => {
      player.on(eventName, () => {
        const duration = player.duration();
        const readyState = player.readyState();
        const networkState = player.networkState();
        const currentSrc = player.currentSrc();
        const bufferedPercent = player.bufferedPercent();

        const readyStateName: Record<number, string | undefined> = {
          0: "HAVE_NOTHING",
          1: "HAVE_METADATA",
          2: "HAVE_CURRENT_DATA",
          3: "HAVE_FUTURE_DATA",
          4: "HAVE_ENOUGH_DATA"
        };
        const readyStateNameString = readyStateName[readyState];

        const networkStateName: Record<number, string | undefined> = {
          0: "NETWORK_EMPTY",
          1: "NETWORK_IDLE",
          2: "NETWORK_LOADING",
          3: "NETWORK_NO_SOURCE"
        };
        const networkStateNameString = networkStateName[networkState];
        const error = player.error();

        const trackData = {
          video_playhead_seconds: Math.round(player.currentTime()),
          duration: isNaN(duration) ? -1 : Math.round(duration),
          fullscreen: player.isFullscreen(),
          currentSrc,
          bufferedPercent,
          readyState,
          readyStateNameString,
          networkState,
          networkStateNameString,
          errorCode: error?.code,
          errorMessage: error?.message,
          textLanguage: currentLanguage ?? "OFF"
        };

        if (eventName === "error") {
          if (this.errorCount === 0) {
            this.trackEvent(eventName, trackData);
            reportErrorSentry(error);

            const providerError = error?.message;

            this.emit({
              ...this.state,
              errorMessage: translate("video.error", {
                context: "tryAgain"
              }),
              providerErrorMessage: providerError
            });
          }

          this.errorCount++;
          setTimeout(() => {
            if (this.videoOptions && this.errorCount >= this.errorLimit) {
              this.reInitVideo(this.containerElement, this.videoOptions);
            }
          }, 1000);
        } else {
          this.emit({
            ...this.state,
            errorMessage: undefined,
            providerErrorMessage: undefined
          });
          this.trackEvent(eventName, trackData);
        }
      });
    });

    player.on("progress", () => {
      this.lastProgressTime = Date.now();
      this.clearNetworkTimeout();
      if (this.state.errorMessage) {
        this.emit({
          ...this.state,
          errorMessage: undefined,
          providerErrorMessage: undefined
        });
      }
    });

    player.on("timeupdate", () => {
      this.lastProgressTime = Date.now();
      this.clearNetworkTimeout();
    });

    // safari does not emit an error event when video is stuck, so we need to rely on stalled and waiting events
    ["stalled", "waiting"].forEach((eventName) => {
      player.on(eventName, () => {
        if (this.isSafari) {
          this.startNetworkTimeout();
        }
      });
    });

    ["canplay", "pause", "ended"].forEach((eventName) => {
      player.on(eventName, () => {
        this.clearNetworkTimeout();
      });
    });
  };

  trackEvent = (
    event: string,
    data: Record<string, boolean | number | string | undefined>
  ) => {
    tracker.track(`Video Player Interaction: ${event}` as TrackEvent, { data });
    this.log(event, data);
  };

  startNetworkTimeout = () => {
    this.clearNetworkTimeout();
    this.lastProgressTime = Date.now();
    this.networkTimeoutId = setTimeout(() => {
      const timeSince = Date.now() - this.lastProgressTime;
      if (timeSince >= 15000 && this.isSafari) {
        this.emit({
          ...this.state,
          errorMessage: translate("video.error", { context: "tryAgain" })
        });
      }
    }, 16000);
  };

  clearNetworkTimeout = () => {
    if (this.networkTimeoutId) {
      clearTimeout(this.networkTimeoutId);
      this.networkTimeoutId = undefined;
    }
  };

  loggingEnabled = false;
  log = (event: string, ...data: unknown[]) => {
    if (!this.loggingEnabled) {
      return;
    }

    console.log(`VIDEO PLAYER EVENT: ${event}`, ...data);
  };

  reInitVideo = (
    containerElement: HTMLVideoElement | null,
    videoOptions: VideoPlayerOptions
  ) => {
    this.disposePlayer();
    void this.initVideo(containerElement, videoOptions);
  };

  reInitVideoFullVideo = (
    containerElement: HTMLVideoElement | null,
    videoOptions: VideoPlayerOptions,
    retry?: boolean
  ) => {
    this.disposePlayer();

    if (retry) {
      if (!videoOptions.sources?.length) {
        // If no sources available during retry, fall back to normal initialization
        void this.initVideo(containerElement, videoOptions);
        return;
      }

      const timestamp = Date.now().toString();
      const cacheTimestampSource = videoOptions.sources.map((source) => {
        const url = new URL(source.src);
        url.searchParams.set("cache", timestamp);
        return {
          ...source,
          src: url.toString()
        };
      });

      const videoOptionsWithCacheTimestamp = {
        ...videoOptions,
        sources: cacheTimestampSource
      };

      void this.initVideo(containerElement, videoOptionsWithCacheTimestamp);
    } else {
      void this.initVideo(containerElement, videoOptions);
    }
  };

  disposePlayer = () => {
    const { player } = this.state;
    if (player && !player.isDisposed()) {
      player.dispose();
    }
    this.clearNetworkTimeout();
    this.containerElement = null;
    this.videoOptions = undefined;
    this.errorCount = 0;
    this.emit({
      ...this.state,
      playing: false,
      ready: false,
      player: undefined,
      errorMessage: undefined,
      providerErrorMessage: undefined
    });
  };
}
